import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, 
  TableBody, TableContainer, Paper, Button, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, Switch, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import licenseService from '../../services/licenseService';
import SkeletonBlock from '../ui/SkeletonBlock';
import { useToast } from '../ToastProvider';

export default function LicenseVendorsAdmin() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', logoUrl: '', isActive: true });
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, vendor: null });
  const toast = useToast();

  useEffect(() => { loadData(); }, [showInactive]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await licenseService.getVendors(showInactive);
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data || e.message || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (vendor = null) => {
    setEditingVendor(vendor);
    setForm(vendor ? { 
      name: vendor.name, 
      description: vendor.description || '', 
      logoUrl: vendor.logoUrl || '',
      isActive: vendor.isActive 
    } : { name: '', description: '', logoUrl: '', isActive: true });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingVendor(null);
    setForm({ name: '', description: '', logoUrl: '', isActive: true });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingVendor) {
        await licenseService.updateVendor(editingVendor.id, form);
        toast.success('Vendor updated');
      } else {
        await licenseService.createVendor(form);
        toast.success('Vendor created');
      }
      closeDialog();
      loadData();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.vendor) return;
    try {
      await licenseService.deleteVendor(deleteConfirm.vendor.id);
      toast.success('Vendor deactivated');
      setDeleteConfirm({ open: false, vendor: null });
      loadData();
    } catch (e) {
      toast.error(e?.response?.data || e.message || 'Failed to delete');
    }
  };

  if (loading) return <SkeletonBlock lines={5} />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">License Vendors</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel
            control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label="Show inactive"
          />
          <IconButton onClick={loadData} size="small"><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
            Add Vendor
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Products</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendors.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center">No vendors found</TableCell></TableRow>
            ) : vendors.map((v) => (
              <TableRow key={v.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {v.logoUrl && <img src={v.logoUrl} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />}
                    <strong>{v.name}</strong>
                  </Box>
                </TableCell>
                <TableCell>{v.description || '-'}</TableCell>
                <TableCell align="center">{v.productCount ?? 0}</TableCell>
                <TableCell>
                  <Chip label={v.isActive ? 'Active' : 'Inactive'} color={v.isActive ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openDialog(v)}><EditIcon /></IconButton>
                  {v.isActive && (
                    <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ open: true, vendor: v })}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth multiline rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Logo URL" value={form.logoUrl} onChange={(e) => setForm({ ...form, logoUrl: e.target.value })} sx={{ mb: 2 }} placeholder="https://..." />
          <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, vendor: null })}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to deactivate <strong>{deleteConfirm.vendor?.name}</strong>?</Typography>
          {deleteConfirm.vendor?.productCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>This vendor has {deleteConfirm.vendor.productCount} active product(s).</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, vendor: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Deactivate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

